import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, FileSpreadsheet, User, Building2, KeyRound, LogIn, ShieldCheck, Trash2 } from "lucide-react";

const ACTION_META = {
  "auth.login":              { label: "Sign in",                 icon: LogIn,           tone: "text-zinc-700" },
  "user.approve":            { label: "User approved",           icon: ShieldCheck,     tone: "text-emerald-700" },
  "user.deny":               { label: "User denied",             icon: ShieldCheck,     tone: "text-red-700" },
  "user.delete":             { label: "User deleted",            icon: Trash2,          tone: "text-red-700" },
  "user.bulk_delete":        { label: "Users bulk-deleted",      icon: Trash2,          tone: "text-red-700" },
  "password_reset.approve":  { label: "Password reset approved", icon: KeyRound,        tone: "text-emerald-700" },
  "password_reset.deny":     { label: "Password reset denied",   icon: KeyRound,        tone: "text-red-700" },
  "bu.delete":               { label: "BU deleted",              icon: Building2,       tone: "text-red-700" },
  "template.submit":         { label: "Template submitted",      icon: FileSpreadsheet, tone: "text-amber-700" },
  "template.owner_approve":  { label: "Template owner-approved", icon: FileSpreadsheet, tone: "text-violet-700" },
  "template.super_approve":  { label: "Template signed off",     icon: FileSpreadsheet, tone: "text-emerald-700" },
  "template.delete":         { label: "Template deleted",        icon: Trash2,          tone: "text-red-700" },
  "template.bulk_delete":    { label: "Templates bulk-deleted",  icon: Trash2,          tone: "text-red-700" },
  "review.manager_submit":   { label: "Review · manager sign-off",  icon: FileSpreadsheet, tone: "text-amber-700" },
  "review.employee_submit":  { label: "Review · employee sign-off", icon: FileSpreadsheet, tone: "text-blue-700" },
  "review.final_submit":     { label: "Review · final sign-off",    icon: FileSpreadsheet, tone: "text-emerald-700" },
};

export default function Audit() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (actionFilter) params.set("action", actionFilter);
      params.set("limit", "500");
      const { data } = await api.get(`/audit?${params.toString()}`);
      setEvents(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Distinct actions present in the loaded set (for the filter dropdown).
  const actions = useMemo(() => {
    const all = new Set(events.map((e) => e.action));
    return Array.from(all).sort();
  }, [events]);

  return (
    <div>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">System</div>
        <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Audit log
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Every approval, deletion and sign-off. Immutable. Filter to find what you need.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="audit-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search summary, email or action…"
            className="w-full border border-zinc-300 pl-9 pr-3 py-2 rounded-sm text-sm"
          />
        </div>
        <select
          data-testid="audit-action-filter"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
        >
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
          ))}
        </select>
        <button
          data-testid="audit-apply-btn"
          onClick={load}
          style={{ background: "var(--brand-primary)" }}
          className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Apply
        </button>
      </div>

      {/* Events list */}
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3 w-44">When</th>
              <th className="text-left px-5 py-3 w-60">Actor</th>
              <th className="text-left px-5 py-3 w-56">Action</th>
              <th className="text-left px-5 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-16 text-center text-zinc-400 text-sm">Loading…</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-16 text-center text-zinc-500 text-sm">No audit events match this filter.</td></tr>
            ) : events.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, icon: User, tone: "text-zinc-700" };
              const Icon = meta.icon;
              const when = new Date(e.created_at);
              return (
                <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-3 text-zinc-600 text-xs tabular-nums">{when.toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-sm">{e.actor_email || "—"}</div>
                    {e.actor_role && (
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{e.actor_role.replace("_", " ")}</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-zinc-700">{e.summary}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && events.length >= 500 && (
        <div className="text-xs text-zinc-500 mt-3">Showing the most recent 500 events. Use the filters to narrow down older entries.</div>
      )}
    </div>
  );
}
